import { Outlet, useLocation } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import TCGate from './components/gates/TCGate'
import Nav from './components/Nav'

/**
 * App shell with gate overlay logic.
 *
 * Per SG1-D4: working surfaces use Bawa Earth; branding surfaces (hero, sponsor-preview)
 * use Sundown Glow overlay. Context-aware switching per SG1-D5; no user toggle.
 *
 * Gate flow (Session 16 incorporation):
 *   - Tier 0 (@dgtl.lk): NO gates. Full access immediately.
 *   - Tier 2+ public / external: TCGate on first visit if tcAccepted = false.
 *   - Sponsor NDA gates render from SponsorPage when the protected API requires acceptance.
 *   - Gates render as overlays over the <Outlet /> content.
 */
export default function App() {
  const { tier, tcAccepted } = useAuth()
  const location = useLocation()
  const pathname = location.pathname === '/' ? '/' : location.pathname.replace(/\/+$/, '')
  const showNav = pathname !== '/knockout'
  const publicSponsorCopyPaths = new Set([
    '/thelicenced',
    '/the-licenced',
    '/thelicensed',
    '/the-licensed',
    '/gold',
    '/goldbyhemas',
    '/gold-by-hemas',
    '/popculture',
    '/pop-culture',
    '/cameralk',
    '/camera-lk',
    '/cookwarelk',
    '/cookware-lk',
  ])
  const isSimplePublicSponsorCopy =
    publicSponsorCopyPaths.has(pathname) ||
    Array.from(publicSponsorCopyPaths).some(path => pathname === `${path}/value-report`)

  // Legal + auth pages are always accessible without gates
  const isExemptFromGates =
    pathname === '/' ||
    pathname === '/privacy' ||
    pathname === '/terms' ||
    pathname === '/signin' ||
    pathname === '/request-access' ||
    pathname === '/home' ||
    pathname === '/prospect' ||
    pathname === '/prospect/value-report' ||
    pathname === '/watch/sneak-peek' ||
    pathname === '/media/yan-vada-sneak-peek.mp4' ||
    pathname === '/account' ||
    pathname === '/notifications' ||
    pathname === '/knockout' ||
    pathname.startsWith('/team') ||
    pathname.startsWith('/admin') ||
    pathname.startsWith('/temporary/') ||
    isSimplePublicSponsorCopy ||
    pathname === '/status'

  // Core team bypasses everything
  if (tier === 0) {
    return (
      <div className="app-shell">
        {showNav && <Nav />}
        <Outlet />
      </div>
    )
  }

  // TC gate: first visit, haven't accepted terms (and not on exempt page)
  if (!tcAccepted && !isExemptFromGates) {
    return (
      <>
        <div className="app-shell">
          {showNav && <Nav />}
          <Outlet />
        </div>
        <TCGate />
      </>
    )
  }

  return (
    <div className="app-shell">
      {showNav && <Nav />}
      <Outlet />
    </div>
  )
}
